import mixpanel from 'mixpanel-browser';
import { getEnvVariable } from '@/utils/get-env-variable';
import { getSessionReplayConfig, type SessionReplayScope } from '@/constants/sessionReplay';
import { logError } from '@/utils/logger';

type PendingTrack = { kind: 'track'; eventName: string; properties?: Record<string, unknown> };
type PendingIdentify = { kind: 'identify'; userId: string };
type PendingPeopleSet = { kind: 'setUserProperties'; properties: Record<string, unknown> };
type PendingCall = PendingTrack | PendingIdentify | PendingPeopleSet;

const MIXPANEL_TOKEN_FALLBACK = '36bc8102bd15fc0a7471651e632a1c1d';

/**
 * Mixpanel's public HTTP track endpoint. Used by the `pagehide` /
 * visibilitychange safety net to flush buffered events *directly* via
 * `sendBeacon` when the user closes the tab during the idle-deferred
 * init window — i.e. before `mixpanel.init()` has run and registered
 * its own unload-flush handlers.
 *
 * This matches the EU `api_host` configured in `mixpanel.init` below
 * (`api-eu.mixpanel.com`), so events sent through this path are ingested
 * identically — into the EU residency project — as events sent through
 * `mixpanel.track`.
 */
const MIXPANEL_TRACK_URL = 'https://api-eu.mixpanel.com/track/';

class MixpanelService {
  private isInitialized: boolean = false;
  /**
   * Perf (C.1): `initialize()` is deferred to idle time. Any calls
   * arriving before init must be buffered and replayed on init,
   * otherwise events fired during the first-paint window would be
   * silently dropped.
   *
   * The companion safety net (`flushPendingViaBeacon`) handles the
   * narrow but real failure mode where the user closes the tab during
   * the idle-deferred init delay — without it, `appcharge_checkout_opened`
   * and any other pre-init events queued here would never reach the
   * wire.
   */
  private pending: PendingCall[] = [];
  /**
   * Guards against double-flushing on mobile, where
   * `visibilitychange→hidden` and `pagehide` can both fire for the
   * same close. Cleared on each successful init/drain so subsequent
   * pre-init bursts (test environments, navigation rebinds) still work.
   */
  private hasFlushedOnHide: boolean = false;

  constructor() {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      this.installUnloadFlushHook();
    }
  }

  /**
   * @param options.sessionRecordingEnabled Resolved
   *   `enable_mixpanel_session_recording` feature-flag value. Session Replay
   *   recording is only armed when this is `true` (AND the env/percent gates
   *   in `getSessionReplayConfig` also pass). Defaults to `false` so recording
   *   is fail-closed if the flag can't be resolved. Event tracking is
   *   unaffected either way.
   * @param options.sessionReplayScope Checkout route initializing Mixpanel;
   *   selects which sampling-percent env var applies. Defaults to `checkout`.
   */
  initialize(options: { sessionRecordingEnabled?: boolean; sessionReplayScope?: SessionReplayScope } = {}): void {
    if (this.isInitialized) {
      return;
    }

    // Use environment variable, fallback to production key if not set
    const mixpanelToken = getEnvVariable('VITE_MIXPANEL_TOKEN', MIXPANEL_TOKEN_FALLBACK);

    if (!mixpanelToken) {
      console.warn('Mixpanel: VITE_MIXPANEL_TOKEN not configured');
      return;
    }

    try {
      mixpanel.init(mixpanelToken, {
        api_host: 'https://api-eu.mixpanel.com',
        debug: getEnvVariable('VITE_ENV') !== 'production',
        // Perf (C.1): `track_pageview: true` fires an extra network track
        // call the moment `mixpanel.init` runs, competing with the
        // critical `/checkout/v3` and Stripe fetches. We already explicitly
        // send `trackCheckoutOpened`, so the implicit page view is just
        // noise on the wire during the most sensitive window.
        track_pageview: false,
        persistence: 'localStorage',
        /**
         * Session Replay (Sprint: Mixpanel replay). Records a small,
         * sampled share of checkout sessions for UX/funnel debugging.
         *
         * Perf: the rrweb recorder bundle is lazy-loaded from
         * cdn.mxpnl.com ONLY when a session is sampled in — the ~98% who
         * aren't sampled never download or run it, so first-paint and
         * payment latency are unaffected. This whole `init` is already
         * `deferIdle`'d, so even the sampling decision is off the
         * critical path.
         *
         * Privacy: card fields (Stripe/Adyen/Braintree/Nuvei) live in
         * cross-origin provider iframes that rrweb cannot read and that
         * we deliberately do NOT allow-list, so they're never captured.
         * All input values are masked and PII text is masked via
         * `mp-mask` / `record_mask_text_selector`. See
         * `@/constants/sessionReplay` for the full rationale.
         */
        ...getSessionReplayConfig(Boolean(options.sessionRecordingEnabled), options.sessionReplayScope),
      });
      this.isInitialized = true;
      this.drainPending();
      // Subsequent unload events are now mixpanel-browser's responsibility
      // (it registers its own beacon flush during init), so clear the
      // guard in case we're re-initialized later (e.g. after `reset()`).
      this.hasFlushedOnHide = false;
      console.log('Mixpanel initialized successfully');
    } catch (error) {
      logError('Failed to initialize Mixpanel', error);
    }
  }

  private drainPending(): void {
    const queued = this.pending;
    this.pending = [];
    for (const call of queued) {
      try {
        if (call.kind === 'track') mixpanel.track(call.eventName, call.properties);
        else if (call.kind === 'identify') mixpanel.identify(call.userId);
        else if (call.kind === 'setUserProperties') mixpanel.people.set(call.properties);
      } catch (error) {
        logError('Failed to replay buffered Mixpanel call', error);
      }
    }
  }

  track(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.isInitialized) {
      this.pending.push({ kind: 'track', eventName, properties });
      // If a previous hidden-flush already fired but the user came
      // back to the tab AND a new track was queued, we want a second
      // hide to flush THIS one too — drop the dedupe guard.
      this.hasFlushedOnHide = false;
      return;
    }

    try {
      mixpanel.track(eventName, properties);
    } catch (error) {
      logError('Failed to track event in Mixpanel', error);
    }
  }

  identify(userId: string): void {
    if (!this.isInitialized) {
      this.pending.push({ kind: 'identify', userId });
      return;
    }

    try {
      mixpanel.identify(userId);
    } catch (error) {
      logError('Failed to identify user in Mixpanel', error);
    }
  }

  setUserProperties(properties: Record<string, unknown>): void {
    if (!this.isInitialized) {
      this.pending.push({ kind: 'setUserProperties', properties });
      return;
    }

    try {
      mixpanel.people.set(properties);
    } catch (error) {
      logError('Failed to set user properties in Mixpanel', error);
    }
  }

  reset(): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      mixpanel.reset();
    } catch (error) {
      logError('Failed to reset Mixpanel', error);
    }
  }

  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Listens for `pagehide` + `visibilitychange→hidden` so we can flush
   * buffered events via `navigator.sendBeacon` if the user closes the
   * tab BEFORE `initialize()` runs (the idle-deferred window).
   *
   * Why both events:
   *   - `pagehide` is the modern reliable unload signal on desktop and
   *     iOS Safari (where `beforeunload` is unreliable).
   *   - `visibilitychange→hidden` fires on iOS when the user swipes
   *     home; the OS may freeze the tab before `pagehide` ever fires,
   *     so we treat hidden as "might not get a second chance".
   *
   * Both are registered with `{ capture: true }` so we run before any
   * other handler that might cancel propagation. The hook is a no-op
   * once `mixpanel.init` has run — at that point the SDK installs its
   * own beacon flush and we'd just be duplicating sends.
   */
  private installUnloadFlushHook(): void {
    const flushIfPending = () => this.flushPendingViaBeacon();
    window.addEventListener('pagehide', flushIfPending, { capture: true });
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'hidden') flushIfPending();
      },
      { capture: true }
    );
  }

  /**
   * Direct HTTP fallback for the unload-before-init window. Sends each
   * pending `track` call as one entry in a Mixpanel `/track/` batch
   * via `navigator.sendBeacon`. No-op after `mixpanel.init` runs (the
   * SDK owns flush from that point on).
   *
   * Limitations (deliberate):
   *   - Only `track` events are flushed. `identify` and
   *     `setUserProperties` set future-event state — losing them on a
   *     same-tab close before any event fires has no observable
   *     consequence.
   *   - Best-effort `distinct_id` derived from a buffered `identify`
   *     if one happens to be queued; otherwise Mixpanel assigns one.
   *     Anonymous attribution is the only loss for the closed-tab user.
   *   - `sendBeacon` queues up to ~64KB; for our event sizes this is
   *     ~hundreds of events worth of budget — well above what could be
   *     buffered in the ~50-300ms init window.
   */
  private flushPendingViaBeacon(): void {
    if (this.hasFlushedOnHide) return;
    if (this.isInitialized) return;
    if (this.pending.length === 0) return;
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;

    const token = getEnvVariable('VITE_MIXPANEL_TOKEN', MIXPANEL_TOKEN_FALLBACK);
    if (!token) return;

    const trackCalls = this.pending.filter((call): call is PendingTrack => call.kind === 'track');
    if (trackCalls.length === 0) return;

    const identifyCall = this.pending.find((call): call is PendingIdentify => call.kind === 'identify');
    const distinctId = identifyCall?.userId;

    const now = Date.now();
    const events = trackCalls.map((call) => ({
      event: call.eventName,
      properties: {
        token,
        time: now,
        $insert_id: generateInsertId(),
        ...(distinctId ? { distinct_id: distinctId } : {}),
        ...(call.properties ?? {}),
      },
    }));

    // Mixpanel accepts form-encoded `data=<json>` POST bodies, which is
    // what `mixpanel-browser` itself sends — using the same shape keeps
    // ingestion identical.
    try {
      const payload = new Blob([`data=${encodeURIComponent(JSON.stringify(events))}`], {
        type: 'application/x-www-form-urlencoded',
      });
      const queued = navigator.sendBeacon(MIXPANEL_TRACK_URL, payload);
      if (queued) {
        this.pending = this.pending.filter((call) => call.kind !== 'track');
        this.hasFlushedOnHide = true;
      }
    } catch {
      // `sendBeacon` itself doesn't throw in practice, but a Blob ctor
      // failure under exotic browser sandboxes shouldn't take down the
      // unload path — we just leave `pending` alone and accept the loss.
    }
  }
}

/**
 * `$insert_id` lets Mixpanel dedupe events server-side if a beacon and
 * a subsequent `mixpanel.track` (post-init) both arrive for the same
 * logical event. Uses `crypto.randomUUID` when available, falls back to
 * a `Math.random` composition otherwise (older Safari, jsdom).
 */
const generateInsertId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

export const mixpanelService = new MixpanelService();
